import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormData, QuotationRequest } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { createQuotation } from '../services/firebaseBilling';
import { sendQuoteNotifications } from '../services/emailService';
import { communicationService } from '../services/communicationService';

const QuotationForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<FormData>();

  const selectedService = watch('serviceType');

  const serviceOptions = [
    { value: 'advanced-ndt', label: 'Advanced NDT Services', category: 'Advanced NDT' },
    { value: 'remote-field-ect', label: 'Remote Field ECT', category: 'Advanced NDT' },
    { value: 'tofd', label: 'TOFD (Time of Flight Diffraction)', category: 'Advanced NDT' },
    { value: 'phased-array', label: 'Phased Array Ultrasonics', category: 'Advanced NDT' },
    { value: 'ultra-wave-lrt', label: 'Ultra Wave LRT', category: 'Advanced NDT' },
    { value: 'tank-floor-scanning', label: 'Tank Floor Scanning', category: 'Advanced NDT' },
    { value: 'eddy-current', label: 'Eddy Current Testing', category: 'Advanced NDT' },
    { value: 'remote-visual', label: 'Remote Visual Inspection', category: 'Advanced NDT' },
    { value: 'oxide-scale', label: 'Oxide Scale Measurement', category: 'Advanced NDT' },
    { value: 'conventional-ndt', label: 'Conventional NDT Services', category: 'Conventional NDT' },
    { value: 'ultrasonic-flaw', label: 'Ultrasonic Flaw Detection', category: 'Conventional NDT' },
    { value: 'thickness-gauging', label: 'Thickness Gauging', category: 'Conventional NDT' },
    { value: 'magnetic-particle', label: 'Magnetic Particle Testing', category: 'Conventional NDT' },
    { value: 'liquid-penetrant', label: 'Liquid Penetrant Testing', category: 'Conventional NDT' },
    { value: 'hardness-testing', label: 'On-Site Hardness Testing', category: 'Conventional NDT' },
    { value: 'coating-thickness', label: 'Coating Thickness Measurement', category: 'Conventional NDT' },
  ];

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const quotationData: QuotationRequest = {
        timestamp: new Date().toISOString(),
        clientName: data.clientName,
        email: data.email,
        serviceType: data.serviceType,
        description: data.description,
        status: 'Pending' as const,
        amount: 0,
        invoiceNumber: '',
        // Additional fields for PDF generation
        clientAddress: data.clientAddress,
        clientGSTN: data.clientGSTN,
        clientState: data.clientState,
        clientStateCode: data.clientStateCode,
        attention: data.attention,
        subject: data.subject
      };

      const quotationId = await createQuotation(quotationData as any);

      // Send email notifications and create internal message (don't block form submission if these fail)
      try {
        await Promise.allSettled([
          sendQuoteNotifications(quotationData),
          communicationService.createNotification({
            userId: 'admin',
            userEmail: 'admin@avis.com',
            title: 'New Quote Request Received',
            message: `New quote request from ${quotationData.clientName} for ${quotationData.serviceType}`,
            type: 'quote_received',
            isRead: false,
            relatedId: quotationId
          })
        ]);
      } catch (error) {
        console.warn('Notification failed, but quote was submitted successfully:', error);
      }

      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Error submitting quotation:', error);
      setSubmitStatus('error');
      setErrorMessage('Failed to submit quotation request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupedServices = serviceOptions.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, typeof serviceOptions>);

  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" message="Submitting your quotation request..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-avis-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Request a Quote
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Get a customized quote for your NDT testing requirements. 
              Our experts will review your needs and provide competitive pricing.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {submitStatus === 'success' && (
              <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-green-800 font-medium">
                    Thank you! Your quotation request has been submitted successfully. 
                    We'll review your requirements and get back to you within 24 hours.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-800 font-medium">
                    {errorMessage || 'An error occurred while submitting your request. Please try again.'}
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Client Information */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Client Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="clientName"
                      {...register('clientName', { 
                        required: 'Full name is required',
                        minLength: { value: 2, message: 'Name must be at least 2 characters' }
                      })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent ${
                        errors.clientName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                    />
                    {errors.clientName && (
                      <p className="mt-1 text-sm text-red-600">{errors.clientName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                {/* Additional Client Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="clientAddress" className="block text-sm font-medium text-gray-700 mb-2">
                      Client Address
                    </label>
                    <input
                      type="text"
                      id="clientAddress"
                      {...register('clientAddress')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                      placeholder="Enter client address"
                    />
                  </div>

                  <div>
                    <label htmlFor="clientGSTN" className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number
                    </label>
                    <input
                      type="text"
                      id="clientGSTN"
                      {...register('clientGSTN')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                      placeholder="Enter GST number"
                    />
                  </div>

                  <div>
                    <label htmlFor="clientState" className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      id="clientState"
                      {...register('clientState')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                      placeholder="Enter state"
                    />
                  </div>

                  <div>
                    <label htmlFor="clientStateCode" className="block text-sm font-medium text-gray-700 mb-2">
                      State Code
                    </label>
                    <input
                      type="text"
                      id="clientStateCode"
                      {...register('clientStateCode')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                      placeholder="Enter state code"
                    />
                  </div>

                  <div>
                    <label htmlFor="attention" className="block text-sm font-medium text-gray-700 mb-2">
                      Attention
                    </label>
                    <input
                      type="text"
                      id="attention"
                      {...register('attention')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                      placeholder="Enter attention person"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      {...register('subject')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent"
                      placeholder="Enter subject"
                    />
                  </div>
                </div>
              </div>

              {/* Service Selection */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Service Requirements</h2>
                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Service Type *
                  </label>
                  <select
                    id="serviceType"
                    {...register('serviceType', { required: 'Please select a service type' })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent ${
                      errors.serviceType ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Choose a service...</option>
                    {Object.entries(groupedServices).map(([category, services]) => (
                      <optgroup key={category} label={category}>
                        {services.map((service) => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {errors.serviceType && (
                    <p className="mt-1 text-sm text-red-600">{errors.serviceType.message}</p>
                  )}
                </div>
              </div>

              {/* Project Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description *
                </label>
                <textarea
                  id="description"
                  rows={6}
                  {...register('description', { 
                    required: 'Project description is required',
                    minLength: { value: 20, message: 'Description must be at least 20 characters' }
                  })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-transparent ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Please provide detailed information about your project requirements, including:
- Project location and accessibility
- Material type and dimensions
- Specific testing requirements
- Timeline and deadlines
- Any special considerations or constraints"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments (Optional)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors duration-200">
                  <div className="space-y-1 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="attachments" className="relative cursor-pointer bg-white rounded-md font-medium text-avis-blue hover:text-avis-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-avis-blue">
                        <span>Upload files</span>
                        <input
                          id="attachments"
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                          {...register('attachments')}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX, JPG, PNG, XLS, XLSX up to 10MB each
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-avis-blue text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-avis-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotationForm;
